import {
  Text,
  Button,
  useModalState,
  Modal,
  InputSelectControlField,
  Primary13Regular,
  Primary13SemiBold,
  IconPlus,
  Flex
} from '@sequencehq/core-components'
import { useState, useMemo, useCallback } from 'react'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

interface Props {
  taxCategoryName: string
  onAddTaxCategory: (values: { numeralTaxCategory: string }) => Promise<void>
}

const AddNumeralTaxCategoryModalUI = ({
  taxCategoryName,
  onAddTaxCategory,
  onClose
}: Props & { onClose: () => void }) => {
  const { fields, queries } = useForm({
    value: {
      numeralTaxCategory: ''
    },
    fieldConfiguration: [
      {
        property: 'numeralTaxCategory',
        validation: [required]
      }
    ]
  })

  // Fetch Numeral tax categories from API
  const {
    data: numeralTaxCategoriesData,
    isLoading: isLoadingCategories,
    isError: hasError
  } = useQuery(dashboardv99990101Client.getNumeralTaxCategories)

  const numeralTaxCategories = numeralTaxCategoriesData?.items || []

  const comboBoxOptions = useMemo(
    () =>
      numeralTaxCategories.map(category => ({
        value: category,
        label: category
      })),
    [numeralTaxCategories]
  )

  const [selectedValue, setSelectedValue] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const canSubmit =
    queries.isValid && selectedValue && !isSubmitting && !isLoadingCategories

  const handleChange = useCallback(
    (value: string) => {
      setSelectedValue(value)
      fields.numeralTaxCategory.onChange(value)
    },
    [fields.numeralTaxCategory]
  )

  const handleSubmit = useCallback(async () => {
    if (selectedValue && !isSubmitting) {
      setIsSubmitting(true)
      try {
        await onAddTaxCategory({
          numeralTaxCategory: selectedValue
        })
        onClose() // Only close if the operation succeeds
      } catch (error) {
        // Error is already handled by the hook (notifications), just don't close the modal
        console.error('Failed to create numeral mapping:', error)
      } finally {
        setIsSubmitting(false)
      }
    }
  }, [selectedValue, isSubmitting, onAddTaxCategory, onClose])

  return (
    <>
      <Modal.Header showCloseButton withBorder onClose={onClose}>
        Add Numeral mapping
      </Modal.Header>
      <Modal.Content minHeight="auto">
        <Flex flexDirection="column" gap={3}>
          <Flex flexDirection="column" gap={2}>
            <Text {...Primary13Regular}>Mapping for:</Text>
            <Text as="span" {...Primary13SemiBold}>
              {taxCategoryName}
            </Text>
          </Flex>
          {hasError && (
            <div
              style={{ color: 'red', fontSize: '14px', marginBottom: '8px' }}
              data-testid="addNumeralMapping.error"
            >
              Failed to load Numeral tax categories. Please try again.
            </div>
          )}
          <InputSelectControlField
            label="Numeral tax category"
            placeholder={
              isLoadingCategories
                ? 'Loading categories...'
                : 'Select a tax category...'
            }
            options={comboBoxOptions}
            initialValue={selectedValue}
            onChange={handleChange}
            disabled={isLoadingCategories}
            data-testid="numeralTaxCategoryComboBox"
          />
        </Flex>
      </Modal.Content>
      <Modal.Footer withBorder>
        <Button
          onClick={onClose}
          style={{ marginLeft: 'auto' }}
          variant="secondary"
          data-testid="addNumeralMapping.cancel"
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          type="button"
          disabled={!canSubmit}
          onClick={handleSubmit}
          data-testid="addNumeralMapping.submit"
        >
          Add tax category
        </Button>
      </Modal.Footer>
    </>
  )
}

const AddNumeralTaxCategoryModal = ({ ...props }: Props) => {
  const { isModalOpen, openModal, closeModal } = useModalState()

  return (
    <>
      <Button
        variant="ghost"
        onClick={openModal}
        leadingIcon={<IconPlus color="inherit" />}
        data-testid="addNumeralMapping.trigger"
      >
        Add Numeral mapping
      </Button>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        data-testid="numeralTaxCategoryModal"
      >
        <AddNumeralTaxCategoryModalUI {...props} onClose={closeModal} />
      </Modal>
    </>
  )
}

export default AddNumeralTaxCategoryModal
